import { useEffect, useState } from 'react';
import { ActivityIndicator, AppState, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Stack } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { AuthProvider, useAuth } from '../auth/AuthContext';
import LoginScreen from '../auth/LoginScreen';
import LockScreen from '../auth/LockScreen';
import WebQrLogin from '../auth/WebQrLogin';
import { ComputadorWeb } from '../components/ComputadorWeb';
import { DEMO } from '../lib/env';
import { LARGURA_COLUNA, useTamanho } from '../lib/tamanho';
import { carregarIdioma, t, useIdioma } from '../lib/i18n';
import { supabase } from '../lib/supabase';
import { Btn, Field } from '../components/ui';

const WEB_REAL = Platform.OS === 'web' && !DEMO;

// Navegador: barras de rolagem visíveis no tema escuro (no Windows elas somem no fundo escuro) e altura certa no celular.
if (Platform.OS === 'web' && typeof document !== 'undefined' && !document.getElementById('mp-rolagem')) {
  const css = document.createElement('style');
  css.id = 'mp-rolagem';
  css.textContent = `
    * { scrollbar-width: thin; scrollbar-color: #4A5263 transparent; }
    ::-webkit-scrollbar { width: 10px; height: 8px; }
    ::-webkit-scrollbar-thumb { background: #4A5263; border-radius: 6px; border: 2px solid transparent; background-clip: padding-box; }
    ::-webkit-scrollbar-thumb:hover { background: #6B7486; background-clip: padding-box; }
    ::-webkit-scrollbar-track { background: transparent; }
    html, body, #root { height: 100%; }
    @supports (height: 100dvh) { html, body, #root { height: 100dvh; } }
  `;
  document.head.appendChild(css);
}
import { TaskSheetProvider } from '../components/TaskSheet';
import { AcoesProvider } from '../components/Acoes';
import { PauseOverlay } from '../components/PauseOverlay';
import { C, aplicarModo } from '../components/theme';
import { PlatformHost } from '../lib/platformHost';
import { avisarPublicacoes, avisarRecebidos, initNotifications, rescheduleAll } from '../notifications';
import { allDocs, onDocChange, syncNow, useDoc } from '../store/store';
import { maybeAutoCheck, puxarCapturas } from '../store/monitor';
import { verificar } from '../modules/monitor/check';
import { procurarAtualizacoes } from '../lib/atualizacoes';
import { sincronizarAgenda } from '../lib/gcal';
import { navegacaoPronta, ouvirToquesPush, registrarPush } from '../lib/push';
import { MOD_IDS } from '../store/types';
import { GruposProvider } from '../components/Grupos';
import { AtualizacaoPopup } from '../components/AtualizacaoPopup';
import { SessaoAviso } from '../components/Reentrar';
import { BoasVindas } from '../components/Termos';
import { ConvitePublico } from '../components/ConvitePublico';
import { IndicacaoPublica } from '../components/IndicacaoPublica';
import { PaginaLegal } from '../components/PaginaLegal';
import { guardarIndicacao, indicacaoDoLink } from '../lib/indicacao';
import { codigoDoLink, guardarConvite, pegarConvite } from '../lib/convite';
import { atualizarRecebidos } from '../lib/grupos';
import { iniciarCompartilhamento, registrarRegras } from '../lib/compartilhar';

registrarRegras();
iniciarProtecao();   // 0.57: guarda erros e percebe quando o app fechou sozinho ao abrir
protegerLinks();     // 0.58: só abre links seguros (bloqueia javascript:, data: e parecidos)
import { Linking } from 'react-native';
import { useRouter } from 'expo-router';
import { fecharCofre } from '../lib/cofre';
import { sincronizarRelogio } from '../lib/relogio';
import { iniciarChat, pararChat } from '../lib/chat';
import { Tocando } from '../components/Tocando';
import { ConfirmarCelular } from '../components/ConfirmarCelular';
import { conferirCelular, useCelular } from '../lib/celular';
import { fechouSeguidas, iniciarProtecao, modoSeguro } from '../lib/falhas';
import { protegerLinks } from '../lib/linkSeguro';
import { AvisoFalha, TelaModoSeguro } from '../components/Falha';
import '../lib/restauros';
import { limparLixeiraVelha } from '../lib/lixeira';
import { FaixaDesfazer } from '../components/Desfazer';
import { MeAviseHost } from '../components/MeAvise';
import { EnvioPlannerHost } from '../components/EnvioPlanner';   // texto 10 (E): "Enviar para o My Planner?"
import { DestaqueGuia } from '../components/guia/Destaque';   // texto 08 (C1): "Mostrar na tela" do guia
import { SessaoOverlay } from '../components/SessaoEstudo';
import { definirEscalaTexto } from '../lib/rnTexto';
import { definirCalendario } from '../lib/date';
import { definirDiscreto } from '../lib/discreto';
import { dinheiro } from '../lib/moeda';
import { moedaDe } from '../store/financas';
import { migrarListasTexto21 } from '../lib/migrarListas';
import { migrarClientes } from '../lib/clientes';
export { ErrorBoundary } from '../components/Falha';

let reagendar: ReturnType<typeof setTimeout> | null = null;
// Agenda do Google: sincroniza um pouco depois de cada alteração (só no celular escolhido).
let agendaTimer: ReturnType<typeof setTimeout> | null = null;
const agendarAgenda = (ms = 12000) => {
  if (agendaTimer) clearTimeout(agendaTimer);
  agendaTimer = setTimeout(() => { sincronizarAgenda().catch(() => {}); }, ms);
};
// Publicações capturadas pelo servidor (08h e 12h): junta depois de sincronizar e avisa se vier algo.
// 0.55: traz as capturas mesmo se a sincronização falhar (antes, uma falha de sincronização deixava as novas paradas no servidor)
const trazerCapturas = () => syncNow().catch(() => {}).then(() => puxarCapturas()).then(n => avisarPublicacoes(n)).catch(() => {});
const agendarAlertas = () => {
  if (reagendar) clearTimeout(reagendar);
  reagendar = setTimeout(() => { rescheduleAll(allDocs()).catch(() => {}); }, 3000);
};

// Link do convite aberto antes do login (site ou celular): guarda o código para depois.
const conviteNaPagina = Platform.OS === 'web' && typeof window !== 'undefined' ? codigoDoLink(window.location.href) : null;
if (conviteNaPagina) guardarConvite(conviteNaPagina);
// Link do "Indique e ganhe" (myplanner.expo.app/baixar?i=CODIGO): página pública para baixar o app.
const paginaBaixar = Platform.OS === 'web' && typeof window !== 'undefined' && /^\/baixar/i.test(window.location.pathname);
// Termos e política de privacidade públicos (myplanner.expo.app/privacidade e /termos).
const paginaLegal = Platform.OS === 'web' && typeof window !== 'undefined' && /^\/(privacidade|termos|excluir-conta)/i.test(window.location.pathname);
const indicacaoNaPagina = Platform.OS === 'web' && typeof window !== 'undefined' ? indicacaoDoLink(window.location.href) : null;

function App() {
  const { status } = useAuth();
  // Modo claro ou escuro (Ajustes > Configuração geral > Tema). Ao trocar, as telas são montadas de novo com as cores novas.
  const modo = (useDoc('ajustes')?.modo ?? 'claro') === 'claro' && status !== 'signedOut' ? 'claro' : 'escuro';   // 0.65 (B17): claro é o padrão
  // 0.67 (D3): Tamanho do texto. Trocar remonta as telas (como o modo claro/escuro).
  const tamTexto = useDoc('ajustes')?.tamTexto ?? 'm';
  definirEscalaTexto(tamTexto);
  // 0.67 (D3): Calendário (semana e formato da hora) e Modo discreto (R$ ••• na tela)
  const ajx = useDoc('ajustes');
  definirCalendario({ semanaComeca: ajx?.semanaComeca, hora12: ajx?.hora12 });
  const moedaApp = moedaDe(useDoc('financas'));
  useEffect(() => {
    const simb = [moedaApp, 'BRL'].map(m => dinheiro(0, m).replace(/[\d.,\s\u00a0\u202f-]/g, '')).filter(Boolean);
    definirDiscreto(!!ajx?.discreto, [...new Set(simb)]);
  }, [ajx?.discreto, moedaApp]);
  aplicarModo(modo);
  // texto 12 B: no navegador largo, as laterais da coluna do app seguem o tema (claro ou escuro)
  useEffect(() => { if (Platform.OS === 'web' && typeof document !== 'undefined') document.body.style.backgroundColor = C.card2; }, [modo]);
  const router = useRouter();
  const dentro = status === 'locked' || status === 'unlocked';
  const [verLogin, setVerLogin] = useState(false);

  // celular: o convite pode chegar pelo link myplanner://convite?c=... com o app fechado
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const ler = (u: string | null) => { const c = codigoDoLink(u); if (c) guardarConvite(c); const i = indicacaoDoLink(u); if (i) guardarIndicacao(i); };
    Linking.getInitialURL().then(ler).catch(() => {});
    const s = Linking.addEventListener('url', e => ler(e.url));
    return () => s.remove();
  }, []);
  // depois de entrar na conta, continua o convite pendente
  useEffect(() => {
    if (status !== 'unlocked') return;
    pegarConvite().then(c => { if (c) setTimeout(() => router.push({ pathname: '/convite', params: { c } } as any), 800); }).catch(() => {});
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dentro) return;
    // modo seguro (0.57): só a sincronização dos dados, sem as tarefas automáticas
    if (modoSeguro()) { const i = setInterval(() => { syncNow().catch(() => {}); }, 120000); setTimeout(() => syncNow().catch(() => {}), 3000); return () => clearInterval(i); }
    initNotifications().then(() => { rescheduleAll(allDocs()); registrarPush(); }).catch(() => {});
    const offToques = ouvirToquesPush();
    setTimeout(() => procurarAtualizacoes().catch(() => {}), 4000);
    const off = onDocChange(id => {
      if (id === 'planner' || id === 'garantias' || id === 'documentos' || id === 'financas' || id === 'ajustes' || id === 'envios' || (MOD_IDS as string[]).includes(id)) agendarAlertas();
      if (id === 'planner') agendarAgenda();
    });
    agendarAgenda(5000);
    setTimeout(trazerCapturas, 3000);
    setTimeout(() => sincronizarRelogio().catch(() => {}), 8000);   // relógio (0.33), se estiver conectado
    let fora = 0;   // o cofre fecha se o app ficar mais de 5 minutos em segundo plano
    const sub = AppState.addEventListener('change', s => { if (s === 'background') fora = Date.now(); if (s === 'active' && fora && Date.now() - fora > 5 * 60000) fecharCofre(); if (s === 'active') { verRecebidos(); trazerCapturas(); sincronizarRelogio().catch(() => {}); agendarAlertas(); procurarAtualizacoes().catch(() => {}); agendarAgenda(3000); } });
    let antes = -1;   // avisa quando aumenta o número de recebidos pendentes
    const verRecebidos = () => atualizarRecebidos().then(n => { if (antes >= 0 && n > antes) avisarRecebidos(n - antes); antes = n; }).catch(() => {});
    setTimeout(verRecebidos, 6000);
    iniciarCompartilhamento();   // dados compartilhados nos grupos (0.25)
    const i = setInterval(() => { syncNow().catch(() => {}); verRecebidos(); }, 120000);
    return () => { off(); sub.remove(); clearInterval(i); offToques(); };
  }, [dentro]);

  useEffect(() => { if (status === 'unlocked' && !modoSeguro()) maybeAutoCheck(verificar).catch(() => {}); }, [status]);
  // aviso tocado com o app bloqueado: abre a conversa assim que desbloquear
  useEffect(() => { navegacaoPronta(status === 'unlocked'); }, [status]);

  // Celular confirmado pelo WhatsApp (0.44): confere uma vez ao entrar
  const cel = useCelular();
  useEffect(() => { if (status === 'unlocked') conferirCelular(); }, [status]);
  // lixeira: o que passou de 30 dias sai de vez (0.65)
  useEffect(() => { if (status === 'unlocked') setTimeout(limparLixeiraVelha, 5000); }, [status]);
  // texto 21 (B4): as listas que iam inteiras para o My Planner (fora Finanças, Professor e Consultório) saem de lá
  useEffect(() => { if (status === 'unlocked') setTimeout(migrarListasTexto21, 7000); }, [status]);
  // texto 25: os clientes do Portal e do Freelancer passam a usar a lista única "Meus clientes" (avisa uma vez quantos)
  useEffect(() => { if (status === 'unlocked') setTimeout(() => { try { migrarClientes(); } catch { /* tenta de novo na próxima vez */ } }, 9000); }, [status]);

  // Mensagens (0.35): conversas em tempo real enquanto o app está aberto
  useEffect(() => {
    if (status === 'signedOut') { pararChat(); return; }
    if (status !== 'unlocked' || modoSeguro()) return;
    if (DEMO) { iniciarChat('eu'); return; }
    supabase?.auth.getUser().then(({ data }) => iniciarChat(data.user?.id ?? null)).catch(() => {});
    const sub = AppState.addEventListener('change', s => { if (s === 'active') supabase?.auth.getUser().then(({ data }) => iniciarChat(data.user?.id ?? null)).catch(() => {}); });
    return () => sub.remove();
  }, [status]);

  if (status === 'loading') return <View style={{ flex: 1, backgroundColor: C.bg, justifyContent: 'center' }}><ActivityIndicator color="#8B5CF6" /></View>;
  if (status === 'signedOut') {
    if (conviteNaPagina && !verLogin) return <><ConvitePublico codigo={conviteNaPagina} onContinuar={() => setVerLogin(true)} /><PlatformHost /></>;
    // texto 12 B: no navegador largo, a entrada fica numa coluna de leitura (não esticada)
    return <>{WEB_REAL ? <WebQrLogin /> : <View style={{ flex: 1, width: '100%', maxWidth: Platform.OS === 'web' ? 560 : undefined, alignSelf: 'center' }}><LoginScreen /></View>}<PlatformHost /></>;
  }

  return (
    <View key={modo + tamTexto + (ajx?.semanaComeca ?? '') + (ajx?.hora12 ? '12' : '') + '|' + (ajx?.tema?.id ?? '') + (ajx?.tema?.destaque ?? '')} style={{ flex: 1, backgroundColor: C.bg }}>
      <StatusBar style={modo === 'claro' ? 'dark' : 'light'} />
      <TaskSheetProvider>
        <GruposProvider>
        <AcoesProvider>
          <Stack screenOptions={{ headerShown: false, contentStyle: { backgroundColor: C.bg }, animation: 'slide_from_right' }} />
          {WEB_REAL && <ComputadorWeb />}
          {status === 'unlocked' && <PauseOverlay />}
          {status === 'unlocked' && <SessaoOverlay />}
          {status === 'unlocked' && <AtualizacaoPopup />}
          {status === 'unlocked' && <SessaoAviso />}
          {status === 'unlocked' && <BoasVindas />}
          {status === 'unlocked' && <Tocando />}
          {modoSeguro() && <Text style={{ position: 'absolute', top: 0, left: 0, right: 0, textAlign: 'center', backgroundColor: C.warn, color: '#000', fontSize: 11, fontWeight: '800', paddingVertical: 2 }}>🛟 {t('Modo seguro: tarefas automáticas desligadas até abrir o app de novo')}</Text>}
          <AvisoFalha />
          {status === 'unlocked' && <FaixaDesfazer />}
          {status === 'unlocked' && <MeAviseHost />}
          {status === 'unlocked' && <EnvioPlannerHost />}
          {status === 'unlocked' && <DestaqueGuia />}
        </AcoesProvider>
        </GruposProvider>
      </TaskSheetProvider>
      {status === 'unlocked' && cel.exigir && <View style={StyleSheet.absoluteFill}><ConfirmarCelular onPronto={() => {}} /></View>}
      {status === 'locked' && <View style={StyleSheet.absoluteFill}><LockScreen /></View>}
      <PlatformHost />
    </View>
  );
}

/** Link de "Esqueci a senha" (chega ao site com #access_token=...&type=recovery). */
function retornoSenha() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const h = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return h.get('type') === 'recovery' && h.get('access_token') ? { access: h.get('access_token')!, refresh: h.get('refresh_token') ?? '' } : null;
}
function NovaSenha({ access, refresh }: { access: string; refresh: string }) {
  const [s1, setS1] = useState(''); const [s2, setS2] = useState('');
  const [msg, setMsg] = useState(''); const [ok, setOk] = useState(false); const [busy, setBusy] = useState(false);
  async function salvar() {
    if (s1.length < 8) return setMsg(t('A senha precisa ter 8 caracteres ou mais.'));
    if (s1 !== s2) return setMsg(t('As senhas não conferem.'));
    if (!supabase) return;
    setBusy(true);
    try {
      const r = await supabase.auth.setSession({ access_token: access, refresh_token: refresh });
      if (r.error) throw r.error;
      const u = await supabase.auth.updateUser({ password: s1 });
      if (u.error) throw u.error;
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      setOk(true);
    } catch (e: any) { setMsg(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  return (
    <View style={{ flex: 1, backgroundColor: C.bg, justifyContent: 'center', padding: 28, gap: 14 }}>
      <Text style={{ fontSize: 44, textAlign: 'center' }}>{ok ? '✅' : '🔑'}</Text>
      <Text style={{ color: C.text, fontSize: 22, fontWeight: '800', textAlign: 'center' }}>{ok ? t('Senha alterada.') : t('Criar uma senha nova')}</Text>
      {ok ? (<>
        <Text style={{ color: C.sub, textAlign: 'center' }}>{t('Entre no app com o seu e-mail e a senha nova.')}</Text>
        <Btn cor="#8B5CF6" label={t('Ir para o My Planner Web')} onPress={() => { window.location.href = '/'; }} />
      </>) : (<>
        <Field value={s1} onChangeText={setS1} placeholder={t('Senha nova (8 caracteres ou mais)')} secureTextEntry autoCapitalize="none" />
        <Field value={s2} onChangeText={setS2} placeholder={t('Repita a senha nova')} secureTextEntry autoCapitalize="none" onSubmitEditing={salvar} />
        {!!msg && <Text style={{ color: C.danger }}>{msg}</Text>}
        <Btn cor="#8B5CF6" label={busy ? t('Aguarde...') : t('Salvar')} disabled={busy} onPress={salvar} />
      </>)}
    </View>
  );
}

/** Volta da autorização do Google Drive/OneDrive (a função "nuvem" manda para cá: ?nuvem=ok|erro). */
function retornoNuvem() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const q = new URLSearchParams(window.location.search);
  const r = q.get('nuvem');
  return r ? { ok: r === 'ok', provedor: q.get('provedor') === 'microsoft' ? 'OneDrive' : 'Google Drive', msg: q.get('msg') ?? '' } : null;
}
function NuvemRetorno({ ok, provedor, msg }: { ok: boolean; provedor: string; msg: string }) {
  const janela = typeof window !== 'undefined' && !!window.opener;
  useEffect(() => { if (janela && ok) setTimeout(() => window.close(), 1500); }, [janela, ok]);
  return (
    <View style={{ flex: 1, backgroundColor: C.bg, alignItems: 'center', justifyContent: 'center', padding: 28, gap: 16 }}>
      <Text style={{ fontSize: 48 }}>{ok ? '✅' : '⚠️'}</Text>
      <Text style={{ color: C.text, fontSize: 22, fontWeight: '800', textAlign: 'center' }}>
        {ok ? t('{nome} conectado!', { nome: provedor }) : t('Não foi possível conectar o {nome}', { nome: provedor })}
      </Text>
      {!!msg && <Text style={{ color: C.sub, textAlign: 'center' }}>{msg}</Text>}
      <Text style={{ color: C.sub, textAlign: 'center' }}>{janela ? t('Esta janela pode ser fechada.') : t('Volte para o app My Planner no celular. A tela de Ajustes atualiza sozinha.')}</Text>
      {!janela && (
        <Pressable onPress={() => { window.location.href = 'myplanner://'; }} style={{ backgroundColor: '#8B5CF6', paddingVertical: 14, paddingHorizontal: 24, borderRadius: 14 }}>
          <Text style={{ color: '#fff', fontWeight: '800' }}>{t('Abrir o app My Planner')}</Text>
        </Pressable>
      )}
      <Pressable onPress={() => { window.location.href = '/'; }}><Text style={{ color: '#8B5CF6', fontWeight: '700' }}>{t('Ir para o My Planner Web')}</Text></Pressable>
    </View>
  );
}

export default function RootLayout() {
  const lang = useIdioma();
  const tamanho = useTamanho();
  // texto 12 A: a página diz em que idioma está (o escolhido no app) e pede ao navegador para não traduzir
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const h = document.documentElement;
    h.lang = lang; h.setAttribute('translate', 'no'); h.classList.add('notranslate');
    if (!document.querySelector('meta[name="google"]')) {
      const m = document.createElement('meta'); m.name = 'google'; m.content = 'notranslate'; document.head.appendChild(m);
    }
  }, [lang]);
  const [pronto, setPronto] = useState(false);
  const [decidiu, setDecidiu] = useState(fechouSeguidas() < 2);
  useEffect(() => { carregarIdioma().finally(() => setPronto(true)); }, []);
  if (!pronto) return <View style={{ flex: 1, backgroundColor: C.bg }} />;
  if (!decidiu) return <TelaModoSeguro onSeguir={() => setDecidiu(true)} />;
  const senha = retornoSenha();
  if (senha) return <NovaSenha {...senha} />;
  const volta = retornoNuvem();
  if (volta) return <NuvemRetorno {...volta} />;
  if (paginaLegal) return <PaginaLegal />;
  if (paginaBaixar) return <IndicacaoPublica codigo={indicacaoNaPagina ?? ''} onContinuar={() => { window.location.href = '/'; }} />;
  // Ao trocar o idioma, as telas são montadas de novo (key) para todos os textos mudarem.
  const app = (
    <AuthProvider>
      <StatusBar style="light" />
      <App key={lang} />
    </AuthProvider>
  );
  if (Platform.OS !== 'web') return app;
  // Texto 12 B: no navegador o app usa a largura da janela (celular: como sempre; tablet: até 760 px;
  // computador: coluna central de 900 px). As laterais ficam na cor do tema, não em preto.
  const largura = LARGURA_COLUNA[tamanho];
  if (!largura) return app;
  return (
    <View style={{ flex: 1, backgroundColor: 'transparent', alignItems: 'center' }}>
      <View style={{ flex: 1, width: '100%', maxWidth: largura, backgroundColor: C.bg, borderLeftWidth: 1, borderRightWidth: 1, borderColor: C.line }}>{app}</View>
    </View>
  );
}
